import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftRight } from 'lucide-react';
import { api } from '@/lib/api';
import { useDebounce } from '@/hooks/useDebounce';
import { cn, numero } from '@/lib/utils';
import { Segmentado } from '@/componentes/ui/segmentado';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { Esqueleto } from '@/componentes/ui/primitivos';

/**
 * O log de movimentação entre posições.
 *
 * Responde a pergunta que o ERP não responde: QUEM pediu. `ESTOQUE_LANCAMENTOS`
 * registra o efeito no estoque, mas o PBS só conhece o usuário que a procedure
 * recebeu — o login da sessão, o IP e o horário exato ficam só aqui.
 *
 * As tentativas RECUSADAS aparecem junto, de propósito. Numa investigação elas
 * costumam ser o que mais importa: o que alguém tentou fazer e o sistema
 * barrou não existe em lugar nenhum do ERP.
 */

interface MovimentacaoAuditada {
  id: number;
  quando: string;
  login: string | null;
  resultado: string;
  produto: number | null;
  produtoDescricao: string | null;
  origem: number | null;
  origemEndereco: string | null;
  destino: number | null;
  destinoEndereco: string | null;
  quantidade: number | null;
  ip: string | null;
  mensagem: string | null;
}

const DIAS = [
  { valor: '1', rotulo: '24 h' },
  { valor: '7', rotulo: '7 dias' },
  { valor: '30', rotulo: '30 dias' },
  { valor: '90', rotulo: '90 dias' },
] as const;

type Periodo = (typeof DIAS)[number]['valor'];

export function LogMovimentacoes({
  centro,
  diasPadrao = '30',
}: {
  centro: number | null;
  /** Período inicial. A aba "Hoje" abre em 24 h; o resto, em 30 dias. */
  diasPadrao?: Periodo;
}): JSX.Element {
  const [dias, definirDias] = React.useState<Periodo>(diasPadrao);
  const [busca, definirBusca] = React.useState('');
  const buscaAtrasada = useDebounce(busca, 350);

  const consulta = useQuery<{ centro: number; movimentacoes: MovimentacaoAuditada[] }>({
    queryKey: ['auditoria', 'movimentacoes', centro, dias, buscaAtrasada],
    queryFn: () =>
      api.get<{ centro: number; movimentacoes: MovimentacaoAuditada[] }>(
        '/api/auditoria/movimentacoes',
        { centro, dias, q: buscaAtrasada.trim() || undefined },
      ),
    enabled: centro !== null,
    staleTime: 30_000,
  });

  const linhas = consulta.data?.movimentacoes ?? [];

  return (
    <div className="cartao flex min-h-0 flex-1 flex-col">
      {/*
        O QUE ESTA ABA É — E O QUE ELA NÃO É.

        Ela foi lida como "todas as movimentações do centro nos últimos X
        dias", e não é: é o log do PRÓPRIO WMS, com as TENTATIVAS feitas por
        aqui e quem as fez. O que foi movido pelo coletor ou direto no ERP não
        aparece — o ERP registra o efeito no estoque, não quem pediu.

        Sem esta linha, a aba parece incompleta quando na verdade está certa, e
        quem audita conclui que faltou dado. O caminho para "tudo que mexeu no
        saldo" é o Kardex, ao lado, e por isso ele é citado por nome.
      */}
      <p className="border-b border-borda bg-superficie2 px-3 py-2 text-[12px] leading-snug text-textoFraco">
        Tudo que foi movido <b className="text-texto">pelo WMS</b> neste centro, com quem pediu e o
        que o ERP respondeu. O que foi lançado pelo coletor ou direto no ERP não entra aqui — para
        o histórico completo de um SKU, use a aba <b className="text-texto">Kardex</b>.
      </p>

      <div className="flex flex-wrap items-center gap-2 border-b border-borda px-3 py-2.5">
        <Segmentado
          tamanho="pequeno"
          valor={dias}
          aoMudar={definirDias}
          rotuloAcessivel="Período"
          opcoes={DIAS.map((d) => ({ valor: d.valor, rotulo: d.rotulo }))}
        />
        <input
          value={busca}
          onChange={(e) => definirBusca(e.target.value)}
          placeholder="produto, posição ou login"
          aria-label="Buscar no log de movimentações"
          className="min-h-[34px] w-[230px] rounded-lg border border-borda bg-superficie px-3 text-[13px]"
        />
        <span className="ml-auto text-[11.5px] text-textoFraco">
          {linhas.length > 0 ? `${numero(linhas.length)} registros` : ''}
        </span>
      </div>

      {consulta.isLoading ? (
        <Esqueleto className="m-3 h-64" />
      ) : consulta.isError ? (
        <div className="p-3">
          <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
        </div>
      ) : linhas.length === 0 ? (
        <div className="p-3">
          <EstadoVazio
            icone={<ArrowLeftRight className="h-7 w-7" />}
            titulo={
              buscaAtrasada.trim() ? 'Nada encontrado' : 'Nenhuma movimentação no período'
            }
            detalhe={
              buscaAtrasada.trim()
                ? 'Procure pelo código do produto, pelo número da posição ou pelo login de quem fez.'
                : 'Toda movimentação feita pelo WMS aparece aqui — inclusive as que o sistema recusou.'
            }
          />
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full min-w-[900px] border-collapse text-[12.5px]">
            <thead className="sticky top-0 bg-superficie">
              <tr className="border-b border-borda text-left">
                <th className="rotulo px-2.5 py-1.5">Quando</th>
                <th className="rotulo px-2.5 py-1.5">Quem</th>
                <th className="rotulo px-2.5 py-1.5">Produto</th>
                <th className="rotulo px-2.5 py-1.5">De</th>
                <th className="rotulo px-2.5 py-1.5">Para</th>
                <th className="rotulo px-2.5 py-1.5 text-right">Qtde</th>
                {/* "Resultado" sozinho não diz de quê. É a resposta do ERP à
                    tentativa: ou o lançamento entrou, ou a regra do Procfit
                    recusou — e aí o motivo vem escrito embaixo. */}
                <th
                  className="rotulo px-2.5 py-1.5"
                  title="O que o ERP respondeu a esta tentativa"
                >
                  O ERP aceitou?
                </th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((m) => (
                <tr
                  key={m.id}
                  className={cn(
                    'border-b border-borda/60 align-top',
                    m.resultado !== 'sucesso' && 'bg-status-divergenciaFundo/40',
                  )}
                >
                  <td className="tabular whitespace-nowrap px-2.5 py-1.5">
                    {new Date(m.quando).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    })}
                  </td>
                  <td className="px-2.5 py-1.5">
                    <span className="block font-semibold">{m.login ?? '—'}</span>
                    {/*
                      O IP fica junto do login: dois acessos com o mesmo login
                      vindo de máquinas diferentes é o primeiro sinal de conta
                      compartilhada, e é o tipo de coisa que só aparece se
                      estiver na mesma linha.
                    */}
                    {m.ip ? (
                      <span className="tabular block text-[10.5px] text-textoSuave">{m.ip}</span>
                    ) : null}
                  </td>
                  <td className="px-2.5 py-1.5">
                    <span className="tabular block font-mono font-semibold">{m.produto ?? '—'}</span>
                    <span className="block max-w-[220px] truncate text-[11px] text-textoFraco">
                      {m.produtoDescricao ?? ''}
                    </span>
                  </td>
                  <td className="px-2.5 py-1.5 font-mono font-semibold">
                    {m.origemEndereco ?? m.origem ?? '—'}
                  </td>
                  <td className="px-2.5 py-1.5 font-mono font-semibold">
                    {m.destinoEndereco ?? m.destino ?? '—'}
                  </td>
                  <td className="tabular px-2.5 py-1.5 text-right font-semibold">
                    {m.quantidade === null ? '—' : numero(m.quantidade)}
                  </td>
                  <td className="px-2.5 py-1.5">
                    <span
                      className={cn(
                        'inline-block rounded px-1.5 py-[3px] text-[11px] font-bold leading-none',
                        m.resultado === 'sucesso'
                          ? 'bg-status-livreFundo text-status-livre'
                          : 'bg-status-divergenciaFundo text-status-divergencia',
                      )}
                    >
                      {m.resultado === 'sucesso'
                        ? 'sim · movido'
                        : m.resultado === 'negado'
                          ? 'não · recusado pela regra'
                          : 'não · falhou'}
                    </span>
                    {/* A frase do ERP é o motivo da recusa — sem ela a linha não explica nada. */}
                    {m.mensagem ? (
                      <span className="mt-0.5 block max-w-[320px] text-[11px] text-textoFraco">
                        {m.mensagem}
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
